interface ICacheEntry<T> {
  version: number;
  cachedAt: number;
  ttlMs: number;
  data: T;
}

const PAGE_BUILDER_PAGE_CACHE_VERSION = 1;
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const memoryCache = new Map<string, ICacheEntry<unknown>>();

const getHost = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.location.host || null;
};

const getKey = (host: string, pageType: string): string =>
  `pageBuilderPageCache:v${PAGE_BUILDER_PAGE_CACHE_VERSION}:${host}:${pageType}`;

const isValidEntry = <T>(entry: ICacheEntry<T>): boolean => {
  if (!entry || typeof entry !== "object") return false;
  if (entry.version !== PAGE_BUILDER_PAGE_CACHE_VERSION) return false;
  if (typeof entry.cachedAt !== "number") return false;
  if (typeof entry.ttlMs !== "number") return false;
  return Date.now() - entry.cachedAt <= entry.ttlMs;
};

export const getCachedPageBuilderPageByType = <T>(pageType: string): T | null => {
  const host = getHost();
  if (!host) return null;

  const key = getKey(host, pageType);
  const cachedInMemory = memoryCache.get(key) as ICacheEntry<T> | undefined;
  if (cachedInMemory && isValidEntry(cachedInMemory)) return cachedInMemory.data;

  if (typeof localStorage === "undefined") return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ICacheEntry<T>;
    if (!isValidEntry(parsed)) return null;

    memoryCache.set(key, parsed as ICacheEntry<unknown>);
    return parsed.data;
  } catch {
    return null;
  }
};

export const setCachedPageBuilderPageByType = <T>(
  pageType: string,
  data: T,
  options?: { ttlMs?: number }
): void => {
  const host = getHost();
  if (!host) return;

  const key = getKey(host, pageType);
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const entry: ICacheEntry<T> = {
    version: PAGE_BUILDER_PAGE_CACHE_VERSION,
    cachedAt: Date.now(),
    ttlMs,
    data,
  };

  memoryCache.set(key, entry as ICacheEntry<unknown>);

  if (typeof localStorage === "undefined") return;

  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Best-effort cache
  }
};

export const clearCachedPageBuilderPageByType = (pageType: string): void => {
  const host = getHost();
  if (!host) return;

  const key = getKey(host, pageType);
  memoryCache.delete(key);

  if (typeof localStorage === "undefined") return;

  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

